import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { platformsData } from '../data/platforms';
import { useInView } from '../hooks';
import GameThumbnail from '../components/ui/GameThumbnail';
import { gamesData } from '../data/games';

const PlatformIcon = ({ id, size = 40 }) => {
  const icons = {
    html5: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <circle cx="12" cy="12" r="11" stroke="#e34c26" strokeWidth="1.5" />
        <text x="12" y="16" textAnchor="middle" fontSize="9" fill="#e34c26" fontWeight="bold">HTML5</text>
      </svg>
    ),
    android: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="#3ddc84">
        <path d="M6,18 L6,11 Q6,8 12,8 Q18,8 18,11 L18,18 Q18,20 16,20 L14,20 L14,22 Q14,23 13,23 L11,23 Q10,23 10,22 L10,20 L8,20 Q6,20 6,18 Z" />
        <circle cx="9" cy="10" r="1.5" fill="white" />
        <circle cx="15" cy="10" r="1.5" fill="white" />
        <line x1="8" y1="6" x2="5" y2="3" stroke="#3ddc84" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="16" y1="6" x2="19" y2="3" stroke="#3ddc84" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    ios: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="white">
        <path d="M16.5,3 Q14,1 11.5,3 Q12,5 15,5 Q17,5 16.5,3 Z" />
        <path d="M7,8 Q5,10 5,13 Q5,18 8,20 Q9.5,21 11,20 Q12.5,19 14,20 Q15.5,21 17,20 Q20,18 20,13 Q20,10 18,8 Q16,6 14,7 Q12.5,7.5 11,7 Q9,6 7,8 Z" />
      </svg>
    ),
    huawei: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="#ff0000">
        <path d="M12,2 L14,8 L12,6 L10,8 Z" />
        <path d="M22,12 L16,14 L18,12 L16,10 Z" />
        <path d="M12,22 L10,16 L12,18 L14,16 Z" />
        <path d="M2,12 L8,10 L6,12 L8,14 Z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    pc: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="#66c0f4" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    xbox: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <circle cx="12" cy="12" r="10" fill="#107c10" />
        <circle cx="12" cy="12" r="10" fill="none" stroke="#0e6b0e" strokeWidth="1.5" />
        <path d="M6,7 Q12,14 18,7" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <path d="M6,17 Q12,10 18,17" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="12" y1="5" x2="12" y2="19" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    playstation: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <circle cx="12" cy="12" r="10" fill="#003791" />
        <text x="12" y="16" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">PS</text>
      </svg>
    ),
    vr: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="#9b59b6" strokeWidth="1.5">
        <rect x="2" y="8" width="20" height="10" rx="5" />
        <circle cx="9" cy="13" r="2.5" fill="#9b59b6" />
        <circle cx="15" cy="13" r="2.5" fill="#9b59b6" />
        <line x1="11.5" y1="13" x2="12.5" y2="13" stroke="#9b59b6" strokeWidth="1" />
      </svg>
    ),
  };
  return icons[id] || null;
};

const PlatformsPage = () => {
  const [active, setActive] = useState(null);
  const [heroRef, heroInView] = useInView(0.1);
  const [gridRef, gridInView] = useInView(0.05);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 md:py-24 relative african-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black" />
        <div ref={heroRef} className={`relative z-10 max-w-7xl mx-auto px-6 text-center transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="section-subtitle text-secondary-400">WHERE WE PLAY</p>
          <h1 className="section-title mb-6">
            OUR <span className="text-gradient">PLATFORMS</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Riven Byte games run everywhere — from your pocket to your living room. Discover
            all the platforms where African gaming comes to life.
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <div className="bg-white/5 border-y border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '8', label: 'Platforms' },
              { value: '16+', label: 'Games Available' },
              { value: '2M+', label: 'Downloads' },
              { value: '50+', label: 'Countries' },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-gaming text-3xl md:text-4xl text-primary-500">{s.value}</div>
                <div className="text-gray-400 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Platforms grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div ref={gridRef} className={`grid md:grid-cols-2 lg:grid-cols-2 gap-8 transition-all duration-1000 ${gridInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {platformsData.map((platform, i) => {
              const platformGames = gamesData.filter(g => g.platforms.includes(platform.id));
              const isActive = active === platform.id;
              return (
                <div
                  key={platform.id}
                  className={`relative overflow-hidden rounded-3xl border transition-all duration-500 cursor-pointer ${
                    isActive
                      ? 'border-white/30 bg-white/10 scale-[1.01]'
                      : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8'
                  }`}
                  style={{ '--platform-color': platform.color }}
                  onClick={() => setActive(isActive ? null : platform.id)}
                >
                  {/* Color accent top bar */}
                  <div className="h-1.5 w-full" style={{ background: platform.color }} />

                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-5">
                        <div className="w-16 h-16 rounded-2xl bg-black/40 flex items-center justify-center border border-white/10 flex-shrink-0">
                          <PlatformIcon id={platform.id} size={36} />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-white">{platform.name}</h2>
                          <p className="text-gray-400 text-sm mt-1">{platform.description}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <div className="font-gaming text-2xl" style={{ color: platform.color }}>{platform.gamesCount}</div>
                        <div className="text-gray-500 text-xs">games</div>
                      </div>
                    </div>

                    <p className="text-gray-400 text-sm leading-relaxed mb-6">{platform.longDescription}</p>

                    {/* Game thumbnails preview */}
                    {platformGames.length > 0 && (
                      <div className="mb-6">
                        <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">Featured Games</p>
                        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                          {platformGames.slice(0, 5).map(game => (
                            <Link
                              key={game.id}
                              to={`/games/${game.slug}`}
                              onClick={e => e.stopPropagation()}
                              className="flex-shrink-0 w-28 rounded-xl overflow-hidden border border-white/10 hover:border-white/30 transition-all hover:scale-105"
                              title={game.title}
                            >
                              <GameThumbnail game={game} className="w-full aspect-video" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                      {platform.storeLink && (
                        <a
                          href={platform.storeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105"
                          style={{ background: platform.color }}
                        >
                          Visit Store ↗
                        </a>
                      )}
                      <Link
                        to={`/games?platform=${platform.id}`}
                        onClick={e => e.stopPropagation()}
                        className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white border border-white/20 hover:bg-white/10 transition-all"
                      >
                        View All Games →
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-primary-500/10 via-transparent to-secondary-400/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-gaming text-3xl md:text-4xl font-bold mb-4">
            READY TO <span className="text-gradient">PLAY?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Browse all our games and find the perfect match for your platform.
          </p>
          <Link to="/games" className="btn-primary inline-block">
            Browse All Games →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PlatformsPage;
